<template>
  <div id="categories-wrapper" class="menu-wrapper">
    <!-- POS Access Required Message -->
    <section v-if="!hasPOSAccess" class="content-container">
      <div class="access-denied-container">
        <h4>POS Access Required</h4>
        <p>
          Menu categories management is part of our POS system add-on. Please
          upgrade your subscription to access this feature.
        </p>
        <router-link to="/settings/billing" class="btn btn-primary">
          Upgrade Subscription
        </router-link>
      </div>
    </section>

    <!-- Regular Menu Categories Content (when access is granted) -->
    <div v-else>
      <alert-modal
        ref="confirmDelete"
        cancelable="true"
        :alertText="alertText"
        @confirm="confirmDelete"
      />

      <b-card-group columns class="menu-card-group mb-4">
        <b-card v-for="menu in localRecord" v-bind:key="menu.id">
          <!-- Tools -->
          <div>
            <toggle-button
              v-if="currentEdits[menu.id] == true"
              class="active-toggle"
              :value="menu.is_active == 1"
              color="#82C7EB"
              :sync="true"
              :labels="{ checked: 'on', unchecked: 'off' }"
              :width="65"
              :height="33"
              style="font-size: 12pt"
              @change="menu.is_active = $event.value ? true : false"
            />
            <b-badge
              class="menu-badge"
              v-if="currentEdits[menu.id] != true"
              :variant="menu.is_active == 1 ? 'success' : 'secondary'"
              >{{ menu.is_active == 1 ? "active" : "inactive" }}</b-badge
            >
          </div>

          <div class="menu-actions">
            <i
              v-if="currentEdits[menu.id] != true"
              class="fi-pencil"
              @click="$set(currentEdits, menu.id, true)"
            />
            <i
              v-if="currentEdits[menu.id] == true"
              class="fi-trash"
              @click="startDelete(menu.id)"
            />
            <i
              v-if="currentEdits[menu.id] == true"
              class="fi-save"
              @click="updateMenu(menu)"
            />
          </div>

          <br />
          <!-- Display Content -->
          <div v-if="currentEdits[menu.id] != true">
            <h4>{{ menu.name }}</h4>
            <span>{{ menu.description }}</span>
            <br /><br />
            <div v-for="tag in menu.menu_tag" v-bind:key="tag.id">
              <b-badge variant="info"> {{ tag.name }} </b-badge>
            </div>
          </div>

          <!-- Edit Content -->
          <div v-else>
            <info-row class="info-main-row" :sizing="2" :title="'Name'">
              <b-col sm="12" class="input-group">
                <b-form-input
                  type="text"
                  :title="'Name'"
                  :placeholder="'Happy Hour Menu'"
                  class="form-control"
                  :value="menu.name"
                  v-model="menu.name"
                  required
                />
              </b-col>
            </info-row>

            <Taggregator
              :label="'select which tags to use for menu categories'"
              ref="tagsTaggregator"
              :items="getTags"
              :selections.sync="selectedTags[menu.id]"
              :minimum="0"
              :name="'tags'"
              labelKey="name"
              :quickAddCallback="quickAddTag"
              :quickAddDescription="'Please provide valid tag name'"
              :quickAddMinWordCount="1"
              :quickAddMinWordLength="1"
              :quickAddHasUniqueName="true"
            >
              <template slot="result" slot-scope="{ result }">
                <span class="result-label">
                  {{ result.name }}
                </span>
              </template>
              <template slot="selection" slot-scope="{ selection }">
                {{ selection.name }}
              </template>
            </Taggregator>
            <br />

            <b-form-group
              label="Menu Description"
              label-for="menuDescriptionTextArea"
            >
              <b-form-textarea
                id="menuDescriptionTextArea"
                type="text"
                placeholder="A delightful assortment of savory dishes..."
                v-model="menu.description"
              />
            </b-form-group>
          </div>
        </b-card>

        <!-- Add new menu -->
        <b-card
          v-if="allowCreateNew"
          class="mb-2"
          title="Create New Menu"
          @click="createNewMenu"
        >
          +
        </b-card>
      </b-card-group>
      <div
        v-if="isRequesting"
        style="
          position: absolute;
          width: 100%;
          height: 100%;
          left: 0px;
          top: 0px;
          background: rgba(255, 255, 255, 0.7);
        "
      >
        <spinner-loader />
      </div>
    </div>
  </div>
</template>

<script>
import AlertModal from "@/components/modals/AlertModal";
import SpinnerLoader from "@/components/widgets/SpinnerLoader";
import Taggregator from "@/components/Taggregator";
import cloneDeep from "lodash.clonedeep";
import InfoRow from "@/components/InfoRow";

import { mapState, mapGetters } from "vuex";

export default {
  data() {
    return {
      activeMenu: 1,
      currentEdits: {},
      deleteMenuId: null,
      alertText: "",
      localRecord: {},
      allowCreateNew: true,
      selectedTags: {},
    };
  },
  async created() {
    // Only load data if user has POS access
    if (this.hasPOSAccess) {
      await this.$store
        .dispatch("getMenus")
        .then(() => {
          this.localRecord = cloneDeep(this.getMenus);
          this.postProcessMenu();
        })
        .catch((err) => {
          console.dir(err);
        });
      await this.$store
        .dispatch("fetchTags")
        .then(() => {})
        .catch((err) => {
          console.dir(err);
        });
    }
  },
  components: {
    AlertModal,
    SpinnerLoader,
    Taggregator,
    InfoRow,
  },
  computed: {
    ...mapState({
      isRequesting: (state) =>
        state.menus.isRequesting || state.tags.isRequesting,
      getMenus: (state) => state.menus.menus,
      getTags: (state) => state.tags.tags,
    }),
    ...mapGetters("subscription", ["hasPOSAccess"]),
  },
  methods: {
    postProcessMenu: function () {
      this.selectedTags = {};
      if (undefined === this.localRecord) {
        return;
      }
      for (
        let menuIndex = 0;
        menuIndex < this.localRecord.length;
        menuIndex++
      ) {
        let menu = this.localRecord[menuIndex];
        for (let tagIndex = 0; tagIndex < menu.menu_tag.length; tagIndex++) {
          let tag = menu.menu_tag[tagIndex];
          let sTags = this.selectedTags[menu.id] || [];
          this.$set(this.selectedTags, menu.id, sTags);

          if ("tag_id" in tag) {
            this.selectedTags[menu.id].push({
              id: tag.tag_id,
              name: tag.name,
              created_at: 0,
              updated_at: 0,
            });
          } else {
            this.selectedTags[menu.id].push({
              id: tag.id,
              name: tag.name,
              created_at: 0,
              updated_at: 0,
            });
          }
        }
      }
    },
    async processMenuTagChanges(menuId, tags, originalId) {
      let tagPromises = [];
      let deleteTagArray = [];
      let id =
        null == originalId || undefined == originalId ? menuId : originalId;
      let newTagArray = cloneDeep(tags[id]);

      if (undefined == newTagArray) {
        return;
      }

      let ogMenuTagsRaw = this.localRecord.filter((menu) => menu.id === menuId);
      let ogMenuTags = [];

      if (undefined != ogMenuTagsRaw) {
        if (0 < ogMenuTagsRaw.length) {
          ogMenuTags = ogMenuTagsRaw[0].menu_tag;
        }
      }

      for (let tagIndex = 0; tagIndex < ogMenuTags.length; tagIndex++) {
        let found = false;
        for (
          let tagArrayIndex = 0;
          tagArrayIndex < newTagArray.length;
          tagArrayIndex++
        ) {
          if (ogMenuTags[tagIndex].tag_id == newTagArray[tagArrayIndex].id) {
            newTagArray.splice(tagArrayIndex, 1);
            found = true;
          }
        }
        if (false === found) {
          deleteTagArray.push(ogMenuTags[tagIndex].id);
        }
      }

      for (let i = 0; i < newTagArray.length; i++) {
        tagPromises.push(
          this.$store.dispatch("createMenuTag", {
            tag_id: newTagArray[i].id,
            menu_id: menuId,
          })
        );
      }
      for (let i = 0; i < deleteTagArray.length; i++) {
        tagPromises.push(
          this.$store.dispatch("deleteMenuTag", deleteTagArray[i])
        );
      }

      Promise.all(tagPromises)
        .then(() => {
          this.$store
            .dispatch("getMenus")
            .then(() => {
              this.localRecord = cloneDeep(this.getMenus);
              this.postProcessMenu();
            })
            .catch((err) => {
              console.dir(err);
            });
        })
        .catch((err) => {
          console.log(err);
        });
    },
    createNewMenu: function () {
      let newMenu = {
        name: "",
        description: "",
        is_active: true,
        id: 0,
      };
      this.$set(this.currentEdits, 0, true);
      this.allowCreateNew = false;
      this.localRecord.push(newMenu);
    },
    quickAddTag: function (value, items) {
      this.$store
        .dispatch("createTag", {
          name: value,
        })
        .then((response) => {
          items.push(response);
          this.$store.dispatch(
            "successNotification",
            "Tag succesfully created!"
          );
        })
        .catch((err) => {
          console.dir(err);
          this.$store.dispatch("errorNotification", "Failed to create tag! :(");
        });
    },
    updateMenu: function (menu) {
      let sendObject = {
        name: menu.name,
        description: menu.description,
        is_active: menu.is_active,
      };
      let tagSelections = cloneDeep(this.selectedTags);
      if (0 != menu.id) {
        sendObject["id"] = menu.id;
        this.$store
          .dispatch("updateMenu", sendObject)
          .then(() => {
            let tempRecords = [];
            if (false == this.allowCreateNew) {
              tempRecords = this.localRecord.filter((menu) => menu.id === 0);
            }
            this.localRecord = cloneDeep(this.getMenus);
            for (let i = 0; i < tempRecords.length; i++) {
              let pushRec = tempRecords[i];
              let selected = this.selectedTags[pushRec.id];
              pushRec["menu_tag"] = selected;
              this.localRecord.push(pushRec);
            }
            this.postProcessMenu();
            this.processMenuTagChanges(menu.id, tagSelections);
            this.$set(this.currentEdits, menu.id, false);
            this.$store.dispatch(
              "successNotification",
              "Menu succesfully updated!"
            );
          })
          .catch((err) => {
            console.dir(err);
            this.$store.dispatch("errorNotification", "Failed to update menu");
          });
      } else {
        this.$store
          .dispatch("createMenu", sendObject)
          .then((response) => {
            this.localRecord = cloneDeep(this.getMenus);
            this.postProcessMenu();
            this.processMenuTagChanges(response.id, tagSelections, 0);
            this.$set(this.currentEdits, menu.id, false);
            this.allowCreateNew = true;
            this.$store.dispatch(
              "successNotification",
              "Menu succesfully created!"
            );
          })
          .catch((err) => {
            console.dir(err);
            this.$store.dispatch("errorNotification", "Failed to create menu");
          });
      }
    },
    startDelete: function (_id) {
      if (0 == _id) {
        this.localRecord.pop();
        this.allowCreateNew = true;
        return;
      }
      this.deleteMenuId = _id;
      this.alertText = "Are you sure you want to delete your menu?";
      this.$refs.confirmDelete.open();
    },
    confirmDelete: function () {
      this.$store
        .dispatch("deleteMenu", this.deleteMenuId)
        .then(() => {
          this.localRecord = cloneDeep(this.getMenus);
          this.postProcessMenu();
          this.$store.dispatch(
            "successNotification",
            "Menu succesfully deleted!"
          );
        })
        .catch((err) => {
          console.dir(err);
          this.$store.dispatch("errorNotification", "Failed to delete menu");
        });
      this.deleteMenuId = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.menu-wrapper {
  display: block;
  position: absolute;
  width: 100%;
  height: 100%;

  .info-main-row {
    margin-bottom: 10px;
  }

  .menu-badge {
    position: absolute;
    top: 5px;
    left: 5px;
  }

  .active-toggle {
    position: absolute;
    top: 5px;
    left: 5px;
  }

  .menu-card-group {
    margin: 12px;
    padding-top: 16px;
    column-count: 2;
  }

  .entry-label-text {
    color: #2c3e50;
  }

  .menu-actions {
    position: absolute;
    top: 8px;
    right: 12px;
    font-size: 18pt;

    i {
      cursor: pointer;

      &:hover {
        opacity: 0.7;
      }
    }
  }
}

.access-denied-container {
  text-align: center;
  padding: 3rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin: 2rem 0;
}

.access-denied-container h4 {
  color: #dc3545;
  margin-bottom: 1rem;
}

.access-denied-container p {
  color: #6c757d;
  margin-bottom: 1.5rem;
}
</style>
